import apiClient from './client'
import { STORAGE_KEYS } from '../utils/constants'

// Error codes for session management
export const AUTH_ERROR_CODES = {
  USER_BLOCKED: 'USER_BLOCKED',
  SESSION_LOCKED: 'SESSION_LOCKED',
  ALREADY_LOGGED_IN: 'ALREADY_LOGGED_IN',
  SESSION_INVALID: 'SESSION_INVALID',
}

// Custom error class with error code
export class AuthError extends Error {
  constructor(message, errorCode = null) {
    super(message)
    this.name = 'AuthError'
    this.errorCode = errorCode
  }
}

// Get user-friendly error message based on error code
export const getAuthErrorMessage = (errorCode) => {
  switch (errorCode) {
    case AUTH_ERROR_CODES.USER_BLOCKED:
      return 'تم حظر حسابك. يرجى التواصل مع الدعم.'
    case AUTH_ERROR_CODES.SESSION_LOCKED:
      return 'تم قفل جلستك. يرجى التواصل مع الدعم لإعادة تعيين الوصول.'
    case AUTH_ERROR_CODES.ALREADY_LOGGED_IN:
      return 'أنت مسجل الدخول بالفعل على جهاز آخر. يرجى تسجيل الخروج من هذا الجهاز أولاً.'
    case AUTH_ERROR_CODES.SESSION_INVALID:
      return 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.'
    default:
      return 'حدث خطأ غير متوقع'
  }
}

export const authApi = {
  login: async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', {
        email,
        password,
        device_type: 'web',
      })
      
      if (response.data.success && response.data.data) {
        const { access_token, user } = response.data.data
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token)
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
      }
      
      return response.data
    } catch (error) {
      // Handle 403 errors with session error codes
      if (error.response?.status === 403 && error.response?.data) {
        const { error_code, message } = error.response.data
        throw new AuthError(message || 'Login failed', error_code)
      }
      throw error
    }
  },

  register: async (data) => {
    try {
      const response = await apiClient.post('/auth/signup', {
        ...data,
        device_type: 'web',
      })
      
      if (response.data.success && response.data.data) {
        const { access_token, user } = response.data.data
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access_token)
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
      }
      
      return response.data
    } catch (error) {
      // Handle 403 errors with session error codes
      if (error.response?.status === 403 && error.response?.data) {
        const { error_code, message } = error.response.data
        throw new AuthError(message || 'Registration failed', error_code)
      }
      throw error
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.USER)
    }
  },

  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/forgot-password', { email })
    return response.data
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/user')
    if (response.data.success && response.data.data) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.data))
    }
    return response.data
  },

  updateProfile: async (data) => {
    const response = await apiClient.post('/user/profile', data)
    if (response.data.success && response.data.data) {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.data))
    }
    return response.data
  },

  getUniversities: async () => {
    const response = await apiClient.get('/universities')
    return response.data
  },

  // Get current session info
  getCurrentSession: async () => {
    const response = await apiClient.get('/auth/session')
    return response.data
  },
}
